import { AxlePosition, AxleSide, TyreReading } from '@/generated/GeneratedApiTypes';

export interface AxleData {
    position: AxlePosition;
    label: string;
    tyres: {
        leftOutside?: TyreReading;
        leftInside?: TyreReading;
        rightInside?: TyreReading;
        rightOutside?: TyreReading;
    };
}

export const AXLE_ORDER: AxlePosition[] = [
    'front_axle',
    'rear_axle_1',
    'rear_axle_2',
    'rear_axle_3',
    'rear_axle_4',
    'rear_axle_5',
    'rear_axle_6',
    'rear_axle_7',
    'rear_axle_8',
    'rear_axle_9',
    'rear_axle_10',
    'rear_axle_11',
    'rear_axle_12',
];

export const getAxleLabel = (position: AxlePosition): string => {
    if (position === 'front_axle') return '1';
    const match = position.match(/rear_axle_(\d+)/);
    return match ? String(Number(match[1]) + 1) : position;
};

export const groupTyresByAxle = (tyres: TyreReading[]): AxleData[] => {
    const axleMap = new Map<AxlePosition, Map<AxleSide, TyreReading>>();

    for (const tyre of tyres) {
        if (tyre.axle_position === 'reserve_wheels') continue;

        if (!axleMap.has(tyre.axle_position)) {
            axleMap.set(tyre.axle_position, new Map());
        }
        axleMap.get(tyre.axle_position)!.set(tyre.axle_side, tyre);
    }

    return AXLE_ORDER.filter((position) => axleMap.has(position)).map((position) => {
        const sides = axleMap.get(position)!;
        return {
            position,
            label: getAxleLabel(position),
            tyres: {
                leftOutside: sides.get('left_outside'),
                leftInside: sides.get('left_inside'),
                rightInside: sides.get('right_inside'),
                rightOutside: sides.get('right_outside'),
            },
        };
    });
};

export const isDualTyre = (axle: AxleData): boolean => {
    return !!(axle.tyres.leftInside || axle.tyres.rightInside);
};

export const formatPressure = (pressure: number | null | undefined): string => {
    if (pressure == null) return '-';
    return `${pressure.toFixed(1)} bar`;
};

export const formatTemperature = (temperature: number | null | undefined): string => {
    if (temperature == null) return '-';
    return `${temperature.toFixed(0)}°C`;
};

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

export const getSyncInfoFromTyres = (tyres: TyreReading[]): { daysAgo: number; mileage: number | null } | null => {
    let oldestTimestamp = Infinity;
    let oldestMileage: number | null = null;

    for (const tyre of tyres) {
        if (!tyre.timestamp) continue;
        const time = new Date(tyre.timestamp).getTime();
        if (time < oldestTimestamp) {
            oldestTimestamp = time;
            oldestMileage = tyre.mileage ?? null;
        }
    }

    if (oldestTimestamp === Infinity) return null;

    const daysAgo = Math.floor((Date.now() - oldestTimestamp) / (24 * 60 * 60 * 1000));
    if (daysAgo < 1) return null;

    return { daysAgo, mileage: oldestMileage };
};

export const formatAxleSide = (side: string, intl?: { formatMessage: (d: { id: string }) => string }): string => {
    if (intl) {
        const keyMap: Record<string, string> = {
            left_outside: 'intl-msg:tyres.axleSide.leftOutside',
            left_inside: 'intl-msg:tyres.axleSide.leftInside',
            right_inside: 'intl-msg:tyres.axleSide.rightInside',
            right_outside: 'intl-msg:tyres.axleSide.rightOutside',
        };
        const key = keyMap[side];
        if (key) return intl.formatMessage({ id: key });
    }
    const map: Record<string, string> = {
        left_outside: 'Left Outside',
        left_inside: 'Left Inside',
        right_inside: 'Right Inside',
        right_outside: 'Right Outside',
    };
    return map[side] ?? side;
};

export const getTemperatureVariant = (status?: string | null): 'default' | 'danger' => {
    return status === 'too_high' ? 'danger' : 'default';
};

export const getPressureStatusKey = (status: string | null | undefined): string | null => {
    if (!status) return null;
    const keyMap: Record<string, string> = {
        extreme_overpressure: 'intl-msg:tyres.status.pressure.extremeOverpressure',
        overpressure: 'intl-msg:tyres.status.pressure.overpressure',
        ok: 'intl-msg:tyres.status.ok',
        under_pressure: 'intl-msg:tyres.status.pressure.underPressure',
        extreme_under_pressure: 'intl-msg:tyres.status.pressure.extremeUnderPressure',
        no_data: 'intl-msg:tyres.status.noData',
    };
    return keyMap[status] ?? null;
};

export const getTemperatureStatusKey = (status: string | null | undefined): string | null => {
    if (!status) return null;
    const keyMap: Record<string, string> = {
        too_high: 'intl-msg:tyres.status.temperature.tooHigh',
        ok: 'intl-msg:tyres.status.ok',
        no_data: 'intl-msg:tyres.status.noData',
    };
    return keyMap[status] ?? null;
};

export const getPressureVariant = (status?: string | null): 'default' | 'danger' | 'warning' => {
    switch (status) {
        case 'extreme_overpressure':
        case 'extreme_under_pressure':
            return 'danger';
        case 'overpressure':
        case 'under_pressure':
            return 'warning';
        default:
            return 'default';
    }
};

export const formatSyncTooltip = (syncInfo: { daysAgo: number; mileage: number | null }): string => {
    const mileageText = syncInfo.mileage != null ? ` at ${syncInfo.mileage.toLocaleString()} km` : '';
    return `The last synchronisation was ${syncInfo.daysAgo} days ago${mileageText}.`;
};
